import React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover'
import { cn } from '@/lib/tailwind.utils'
import { Button } from '@src/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/components/ui/command'

export type Region = {
  region: string
  region_name: string
  group_name: string
  max_x: number
  max_y: number
  min_x: number
  min_y: number
}

export type RegionsResponse = Region[]

export type RegionsSelectorProps = {
  value?: string | null
  onChange?: (region: Region | null) => void
}

export const fetchRegions = async (): Promise<RegionsResponse> => {
  const response = await apiClient.get<RegionsResponse>(Endpoint.getRegions)
  return response.data
}

export const useRegionsSelector = () => {
  return useQuery({
    queryKey: ['regions'],
    queryFn: fetchRegions,
  })
}

const RegionsSelector = ({ value, onChange }: RegionsSelectorProps) => {
  const [open, setOpen] = React.useState(false)
  const { data: regions = [], isLoading } = useRegionsSelector()

  const selectedRegion = regions.find((region) => region.region === value) ?? null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={isLoading}
        >
          {selectedRegion?.region_name ?? 'Select region'}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput className="text-foreground" placeholder="Search regions..." />
          <CommandList>
            <CommandEmpty>No regions found.</CommandEmpty>
            <CommandGroup>
              {regions.map((region) => (
                <CommandItem
                  key={region.region}
                  value={`${region.region_name} ${region.region} ${region.group_name}`}
                  onSelect={() => {
                    onChange?.(region.region === selectedRegion?.region ? null : region)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      selectedRegion?.region === region.region ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{region.region_name}</span>
                    <span className="text-xs text-muted-foreground">{region.group_name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default RegionsSelector
